import { useEffect, useState } from 'react';
import type { DeviceRow, PinWrap, TabletRow, UserRow } from '../lib/api';
import { api, ApiError } from '../lib/api';
import { assertPinShape, cacheLogins, pinInUse, preparePin, weakPinReason } from '../lib/credential';
import { formatDate, formatRs } from '../lib/money';
import type {
  CategoryRow, EpfPeriod, PayCycle, RatesRow, RoleInput, WorkerRow,
} from '../lib/roster';
import { pctFromX100, pctToX100, rosterApi } from '../lib/roster';
import type { CostCode, CostCodeRow } from '../lib/costcodes';
import { codeOf, costCodeApi } from '../lib/costcodes';
import CostCodeSelect from '../components/CostCodeSelect';
import WhatsAppCard from '../components/WhatsAppCard';

/**
 * Settings — the owner's admin screen, in the prototype's order:
 *
 *   Workers and salaries · EPF and ETF rates · Plants · Who can log in
 *
 * plus a misc-categories card (CLAUDE.md: "an owner-managed list with a must
 * be allocated to a plant flag per category" — the prototype seeds them and
 * never lets the owner change them) and the tablets and recovery cards that
 * came with device enrolment.
 *
 * Wording is the prototype's `viewSettings()` and `editWorker()`. Differences,
 * all deliberate (CLAUDE.md, "Corrections to the prototype"):
 *
 *   * No PIN field on a login. The prototype stores `u.pin` in the clear.
 *   * Delete disables, for logins and workers alike (plants live on the Permits
 *     tab now, and disable the same way). Nothing the audit
 *     trail names is removed, and a day already costed to a plant keeps it.
 *   * A job role keeps its id through an edit. The prototype rebuilds the list
 *     and re-indexes by position, which is rule 2's "never a positional index".
 *   * No "Start a trial" card. Demo data is a dev-database concern.
 */

const PERMISSION_LABELS: Record<string, string> = {
  labour: 'Enter labour and attendance',
  advance: 'Record advance payments',
  diesel: 'Enter diesel litres',
  rate: 'Change the diesel rate',
  misc: 'Enter misc expenses',
  report: 'View and print the day report',
  summary: 'See cost summary and totals',
  payrun: 'Prepare pay runs under Labour (owner still approves them)',
  plants: 'Add or remove plants',
};

interface Props {
  onBack: () => void;
  currentUserId: string;
  /** This tablet's registration. */
  tabletId: string;
  /** A login was added or a PIN set: the login screen's list has changed. */
  onPeopleChanged: () => void;
}

type Editing =
  | { kind: 'user'; user: UserRow | null }
  | { kind: 'worker'; worker: WorkerRow | null }
  | null;

interface TabletRegistrationRow {
  id: string;
  label: string | null;
  expires_at: string;
  created_at: string;
}

export default function Settings({ onBack, currentUserId, tabletId, onPeopleChanged }: Props) {
  const [users, setUsers] = useState<UserRow[]>([]);
  const [devices, setDevices] = useState<DeviceRow[]>([]);
  const [tablets, setTablets] = useState<TabletRow[]>([]);
  const [tabletRegs, setTabletRegs] = useState<TabletRegistrationRow[]>([]);
  const [workers, setWorkers] = useState<WorkerRow[]>([]);
  const [rates, setRates] = useState<RatesRow | null>(null);
  const [payDay, setPayDay] = useState(12);
  const [categories, setCategories] = useState<CategoryRow[]>([]);
  const [costCodeRows, setCostCodeRows] = useState<CostCodeRow[]>([]);
  const [today, setToday] = useState('');
  const [currentDeviceId, setCurrentDeviceId] = useState('');
  const [editing, setEditing] = useState<Editing>(null);
  const [issued, setIssued] = useState<{ code: string; what: string; who: string } | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  async function refresh() {
    try {
      const [u, d, w, r, c, t, tr, cc] = await Promise.all([
        api.listUsers(),
        api.listDevices(),
        rosterApi.listWorkers(),
        rosterApi.rates(),
        rosterApi.listCategories(),
        api.listTablets(),
        api.listTabletRegistrations(),
        costCodeApi.list(),
      ]);
      setCostCodeRows(cc.costCodes);
      setUsers(u.users);
      setDevices(d.devices);
      setCurrentDeviceId(d.currentDeviceId);
      setTablets(t.tablets);
      setTabletRegs(tr.registrations);
      setWorkers(w.workers);
      setRates(r.current);
      setPayDay(r.payDay);
      setToday(r.today);
      setCategories(c.categories);
      setError('');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load settings.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  async function act(work: () => Promise<unknown>) {
    setError('');
    try {
      await work();
      await refresh();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That did not work. Try again.');
    }
  }

  // The pickers' shape: what /reference sends.
  const costCodes: CostCode[] = costCodeRows.map((row) => ({
    id: row.id, code: row.code, name: row.name, is_active: row.isActive ? 1 : 0,
  }));

  if (loading) return <div className="wrap"><div className="empty">Loading…</div></div>;

  return (
    <div className="wrap">
      <div className="bar" style={{ margin: '12px 0' }}>
        <button type="button" className="btn" onClick={onBack}>
          ‹ Back to the app
        </button>
      </div>

      {error && <div className="banner b-no">{error}</div>}

      {/* A code is readable exactly once. */}
      {issued && (
        <div className="card" style={{ borderColor: 'var(--accent)' }}>
          <h3>{issued.what}</h3>
          <div className="sub" style={{ marginBottom: 10 }}>
            For {issued.who}. Read it out now — it is shown once and only its hash is kept.
          </div>
          <div className="code">{issued.code}</div>
          <div className="bar" style={{ marginTop: 12 }}>
            <button type="button" className="btn pri" onClick={() => setIssued(null)}>
              I have written it down
            </button>
          </div>
        </div>
      )}

      {editing?.kind === 'user' ? (
        <UserEditor
          user={editing.user}
          allPermissions={Object.keys(PERMISSION_LABELS)}
          isSelf={editing.user !== null && editing.user.id === currentUserId}
          onCancel={() => setEditing(null)}
          onSave={async (body) => {
            const user = editing.user;
            await act(async () => {
              // The PIN is wrapped here, on this phone; the server never sees it.
              // PINs are unique across people (the pad works out whose a PIN is),
              // so check it against everyone else's first — from a fresh list.
              if (body.pin) {
                try {
                  await cacheLogins((await api.logins()).logins);
                } catch {
                  /* no signal: check against the cache as it stands */
                }
                if (await pinInUse(body.pin, user?.id)) {
                  throw new ApiError(422, { error: { code: 'invalid', message: 'That PIN is already in use by somebody else. Pick another.' } });
                }
              }
              const pin = body.pin ? (await preparePin(body.pin)).pin : undefined;
              if (!user) {
                if (!pin) throw new ApiError(422, { error: { code: 'invalid', message: 'A new login needs a PIN.' } });
                await api.createUser({ name: body.name, role: body.role, permissions: body.permissions, pin });
              } else {
                await api.updateUser(user.id, {
                  name: body.name, role: body.role, permissions: body.permissions, isActive: body.isActive,
                  ...(pin ? { pin } : {}),
                  rowVersion: user.rowVersion,
                });
              }
              setEditing(null);
              onPeopleChanged();
            });
          }}
          onDisable={
            editing.user === null || editing.user.id === currentUserId
              ? null
              : async () => {
                  const user = editing.user as UserRow;
                  await act(async () => {
                    await api.disableUser(user.id);
                    setEditing(null);
                  });
                }
          }
        />
      ) : editing?.kind === 'worker' ? (
        <WorkerEditor
          worker={editing.worker}
          today={today}
          costCodes={costCodes}
          onCancel={() => setEditing(null)}
          onSave={async (body) => {
            const worker = editing.worker;
            await act(async () => {
              if (!worker) await rosterApi.createWorker(body);
              else await rosterApi.updateWorker(worker.id, { ...body, rowVersion: worker.rowVersion });
              setEditing(null);
            });
          }}
        />
      ) : (
        <>
          {/* -------------------------------------------------------- Cost codes */}
          <CostCodesCard
            costCodes={costCodeRows}
            onAdd={(body) => act(() => costCodeApi.create(body))}
            onUpdate={(row, body) => act(() => costCodeApi.update(row.id, { ...body, rowVersion: row.rowVersion }))}
            onRemove={(row) => act(() => costCodeApi.remove(row.id))}
          />

          {/* ------------------------------------------- Workers and salaries */}
          <div className="card">
            <h3>Employees and salaries</h3>
            <div className="sub">
              You set the daily salary. Overtime is always salary ÷ 8 per hour, and the office
              enters allowances day by day.
            </div>

            {workers.length === 0 && (
              <div className="empty">No employees yet. Add the first one below.</div>
            )}
            {workers.map((worker) => (
              <div
                className="row tap"
                key={worker.id}
                role="button"
                tabIndex={0}
                onClick={() => setEditing({ kind: 'worker', worker })}
                onKeyDown={(e) => { if (e.key === 'Enter') setEditing({ kind: 'worker', worker }); }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="name">
                    {worker.name}
                    {!worker.isActive && <span className="pill p-no" style={{ marginLeft: 8 }}>Inactive</span>}
                  </div>
                  <div className="sub">
                    {worker.roles.map((role, index) => (
                      <span key={role.id}>
                        {index > 0 && '  ·  '}
                        {role.name} {formatRs(role.dailyRateRs)}{' '}
                        <span style={{ opacity: 0.7 }}>(base {formatRs(role.baseRs)})</span>
                      </span>
                    ))}
                    {worker.roles.length === 0 && <span>No job role</span>}
                    {' · '}
                    {worker.payCycle === 'monthly' ? 'Monthly' : 'Weekly'}
                    {codeOf(costCodes, worker.costCodeId) && ` · ${codeOf(costCodes, worker.costCodeId)}`}
                    {' · '}
                    {worker.epf ? (
                      <>
                        <span className="pill p-ok">
                          EPF {worker.epf.epfNo} from {formatDate(worker.epf.memberFrom)}
                        </span>
                        <span className="sub">
                          {worker.epfSalaryRs
                            ? ` · EPF salary ${formatRs(worker.epfSalaryRs)} ${worker.payCycle === 'monthly' ? 'a month' : 'a week'}`
                            : ' · no EPF salary set'}
                        </span>
                      </>
                    ) : (
                      <span className="sub">no EPF</span>
                    )}
                    {worker.lastMembership?.stoppedOn && (
                      <span className="pill p-no" style={{ marginLeft: 6 }}>
                        ended {formatDate(worker.lastMembership.stoppedOn)}
                      </span>
                    )}
                  </div>
                </div>
                <span className="sub">Edit ›</span>
              </div>
            ))}

            <div className="bar" style={{ marginTop: 12 }}>
              <button type="button" className="btn pri" onClick={() => setEditing({ kind: 'worker', worker: null })}>
                Add new employee
              </button>
            </div>
          </div>

          {/* ------------------------------------------------- EPF and ETF rates */}
          <RatesCard
            rates={rates}
            today={today}
            payDay={payDay}
            onSave={(body) => act(() => rosterApi.saveRates(body))}
            onSavePayDay={(day) => act(() => rosterApi.setPayDay(day))}
          />

          {/* ---------------------------------------------------- Misc categories */}
          <CategoriesCard
            categories={categories}
            onAdd={(body) => act(() => rosterApi.createCategory(body))}
            onToggleFlag={(cat) =>
              act(() => rosterApi.updateCategory(cat.id, { requiresPlant: !cat.requiresPlant, rowVersion: cat.rowVersion }))
            }
            onRemove={(cat) => act(() => rosterApi.updateCategory(cat.id, { isActive: false, rowVersion: cat.rowVersion }))}
          />

          {/* ---------------------------------------------- WhatsApp reminders */}
          <WhatsAppCard />

          {/* ------------------------------------------------------ Who can log in */}
          <div className="card">
            <h3>Who can log in</h3>
            <div className="sub">
              A login is a person with a PIN. You set the PIN here when you add them; from then
              on they tap their name on any registered tablet and type it. The PIN itself is
              never stored or sent anywhere — only a credential locked behind it.
            </div>

            {users.map((user) => (
              <div className="row" key={user.id}>
                <div style={{ flex: 1 }}>
                  <div className="name">
                    {user.name}
                    {!user.isActive && <span className="pill p-no" style={{ marginLeft: 8 }}>Disabled</span>}
                    {user.id === currentUserId && (
                      <span className="pill p-info" style={{ marginLeft: 8 }}>You</span>
                    )}
                  </div>
                  <div className="sub">
                    {user.role === 'owner'
                      ? 'full access to everything'
                      : `${user.permissions.length} of ${Object.keys(PERMISSION_LABELS).length} areas`}
                    {' · '}
                    {user.hasPin
                      ? user.deviceCount === 0
                        ? 'PIN set · not used a tablet yet'
                        : `PIN set · used ${user.deviceCount} tablet${user.deviceCount === 1 ? '' : 's'}`
                      : 'no PIN yet — set one under Edit'}
                  </div>
                </div>
                <button type="button" className="btn sm" style={{ minWidth: 0 }} onClick={() => setEditing({ kind: 'user', user })}>
                  Edit ›
                </button>
              </div>
            ))}

            <div className="bar" style={{ marginTop: 12 }}>
              <button type="button" className="btn pri" onClick={() => setEditing({ kind: 'user', user: null })}>
                Add a login
              </button>
            </div>
          </div>

          {tabletRegs.length > 0 && (
            <div className="card">
              <h3>Tablets waiting to be registered</h3>
              <div className="sub">Tablet codes that have been given out but not used yet.</div>
              {tabletRegs.map((reg) => (
                <div className="row" key={reg.id}>
                  <div style={{ flex: 1 }}>
                    <div className="name">Tablet code{reg.label ? ` · ${reg.label}` : ''}</div>
                    <div className="sub">
                      expires {new Date(reg.expires_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </div>
                  <button
                    type="button"
                    className="btn sm dang"
                    style={{ minWidth: 0 }}
                    onClick={() => void act(() => api.cancelTabletRegistration(reg.id))}
                  >
                    Cancel
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* ----------------------------------------------------------- Tablets */}
          <div className="card">
            <h3>Tablets</h3>
            <div className="sub">
              Setting up a device is separate from adding people. Register a tablet once with a
              tablet code and everyone with a PIN can use it. Removing a tablet is the answer to
              a lost one — every PIN stops working on it at once.
            </div>
            {tablets.length === 0 && <div className="empty">No tablets registered.</div>}
            {tablets.map((t) => (
              <div className="row" key={t.id}>
                <div style={{ flex: 1 }}>
                  <div className="name">
                    {t.label}
                    {t.id === tabletId && <span className="pill p-info" style={{ marginLeft: 8 }}>This tablet</span>}
                  </div>
                  <div className="sub">
                    {t.people.length === 0 ? 'not used yet' : `used by ${t.people.map((p) => p.name).join(' · ')}`}
                    {' · registered '}{formatDate(t.registeredAt.slice(0, 10))}
                    {t.lastSeenAt ? ` · last seen ${formatDate(t.lastSeenAt.slice(0, 10))}` : ''}
                  </div>
                </div>
                <button
                  type="button"
                  className="btn sm dang"
                  style={{ minWidth: 0 }}
                  disabled={t.id === tabletId}
                  title={t.id === tabletId ? 'You are using this tablet' : undefined}
                  onClick={() => void act(() => api.revokeTablet(t.id))}
                >
                  Remove
                </button>
              </div>
            ))}
            <div className="bar" style={{ marginTop: 12 }}>
              <button
                type="button"
                className="btn"
                onClick={() =>
                  void act(async () => {
                    const result = await api.createTabletRegistration('Tablet');
                    setIssued({ code: result.code, what: 'Tablet code', who: 'a new tablet' });
                  })
                }
              >
                Register a tablet
              </button>
            </div>
          </div>

          {/* ------------------------------------------ who has used which tablet */}
          <div className="card" style={{ display: devices.length === 0 ? 'none' : undefined }}>
            <h3>Recent sign-ins</h3>
            <div className="sub">
              One line per person per tablet. Remove one to end that person&apos;s sessions on
              that tablet; their PIN still works next time.
            </div>
            {devices.map((device) => (
              <div className="row" key={device.id}>
                <div style={{ flex: 1 }}>
                  <div className="name">
                    {device.user_name}
                    {device.id === currentDeviceId && (
                      <span className="pill p-info" style={{ marginLeft: 8 }}>You, here</span>
                    )}
                  </div>
                  <div className="sub">
                    {device.tablet_label ?? device.label} · set up {formatDate(device.enrolled_at.slice(0, 10))}
                    {device.last_unlock_at
                      ? ` · last used ${formatDate(device.last_unlock_at.slice(0, 10))}`
                      : ' · never used'}
                  </div>
                </div>
                <button
                  type="button"
                  className="btn sm dang"
                  style={{ minWidth: 0 }}
                  onClick={() => void act(() => api.revokeDevice(device.id))}
                >
                  Remove
                </button>
              </div>
            ))}
          </div>

          <div className="card">
            <h3>Recovery code</h3>
            <div className="sub">
              If you lose your tablet and there is no other owner to set you up again, this
              code is the only way back in. Keep it somewhere safe, away from the tablet.
            </div>
            <div className="bar" style={{ marginTop: 12 }}>
              <button
                type="button"
                className="btn"
                onClick={() =>
                  void act(async () => {
                    const result = await api.issueRecoveryCode();
                    setIssued({ code: result.code, what: 'Recovery code', who: result.user });
                  })
                }
              >
                Give me a new recovery code
              </button>
            </div>
            <div className="sub" style={{ marginTop: 8 }}>
              A new one replaces the old one, and it works once.
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ------------------------------------------------------------ worker editor

type WorkerBody = {
  name: string; roles: RoleInput[]; payCycle: PayCycle; epf: EpfPeriod | null; isActive: boolean;
  costCodeId: string | null;
};

interface WorkerEditorProps {
  worker: WorkerRow | null;
  today: string;
  costCodes: CostCode[];
  onCancel: () => void;
  onSave: (body: WorkerBody) => Promise<void>;
}

interface RoleDraft {
  id: string | null;
  name: string;
  base: string;
  addon: string;
}

/** The prototype's `editWorker()`: name, role rows, EPF, active. */
function WorkerEditor({ worker, today, costCodes, onCancel, onSave }: WorkerEditorProps) {
  const [name, setName] = useState(worker?.name ?? '');
  const [costCodeId, setCostCodeId] = useState<string | null>(worker?.costCodeId ?? null);
  const [roles, setRoles] = useState<RoleDraft[]>(
    worker && worker.roles.length > 0
      ? worker.roles.map((r) => ({ id: r.id, name: r.name, base: String(r.baseRs), addon: String(r.addonRs) }))
      : [{ id: null, name: '', base: '3000', addon: '0' }],
  );
  // The prototype defaults a new man to "EPF member, from today". The period
  // shown for an existing man is the one in force, or the last one if it ended.
  const period = worker ? worker.epf ?? worker.lastMembership : null;
  // How he is paid comes first and stands on its own; the EPF tick no longer
  // decides it (1 October 2026). A new man defaults to weekly.
  const [payCycle, setPayCycle] = useState<PayCycle>(worker?.payCycle ?? 'weekly');
  const [isMember, setIsMember] = useState(worker ? worker.epf !== null : true);
  const [epfNo, setEpfNo] = useState(period?.epfNo ?? '');
  const [epfSalary, setEpfSalary] = useState(worker?.epfSalaryRs ? String(worker.epfSalaryRs) : '');
  const [memberFrom, setMemberFrom] = useState(period?.memberFrom ?? today);
  const [stoppedOn, setStoppedOn] = useState(period?.stoppedOn ?? '');
  const [isActive, setIsActive] = useState(worker?.isActive ?? true);
  const [problem, setProblem] = useState('');
  const [saving, setSaving] = useState(false);

  function setRole(index: number, patch: Partial<RoleDraft>) {
    setRoles((held) => held.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  }

  function validate(): WorkerBody | null {
    if (name.trim().length === 0) return fail('Give him a name.');
    const cleaned: RoleInput[] = [];
    for (const r of roles) {
      if (r.name.trim().length === 0 && r.base.trim() === '' && r.addon.trim() === '') continue;
      if (r.name.trim().length === 0) return fail('Every job role needs a name.');
      const base = Number(r.base);
      const addon = r.addon.trim() === '' ? 0 : Number(r.addon);
      if (!Number.isInteger(base) || base < 0) return fail(`Base for ${r.name.trim()} must be a whole number of rupees.`);
      if (!Number.isInteger(addon) || addon < 0) return fail(`Add-on for ${r.name.trim()} must be a whole number of rupees.`);
      cleaned.push({ id: r.id, name: r.name.trim(), baseRs: base, addonRs: addon });
    }
    if (cleaned.length === 0) return fail('Give him at least one job role.');

    let epf: EpfPeriod | null = null;
    if (isMember) {
      if (epfNo.trim().length === 0) return fail('An EPF member needs an EPF number.');
      if (!memberFrom) return fail('A member cannot be saved without a Member from date.');
      if (stoppedOn && stoppedOn < memberFrom) return fail('Stopped on cannot be before Member from.');
      const salary = Math.trunc(Number(epfSalary) || 0);
      if (salary <= 0) {
        return fail(`An EPF member needs an EPF salary: the ${payCycle === 'monthly' ? 'monthly' : 'weekly'} salary his EPF/ETF is worked out on.`);
      }
      epf = { epfNo: epfNo.trim(), memberFrom, stoppedOn: stoppedOn || null, salaryRs: salary };
    }
    return { name: name.trim(), roles: cleaned, payCycle, epf, isActive, costCodeId };

    function fail(message: string): null {
      setProblem(message);
      return null;
    }
  }

  const changes = worker?.membershipChanges ?? [];

  return (
    <div className="card">
      <h3 style={{ marginBottom: 10 }}>{worker ? 'Edit employee' : 'New employee'}</h3>

      <div className="sub" style={{ marginBottom: 4 }}>Name</div>
      <input type="text" value={name} onChange={(e) => setName(e.target.value)} />

      <div className="sub" style={{ margin: '14px 0 2px' }}>Job roles and daily salary</div>
      <div className="sub" style={{ marginBottom: 6 }}>
        One row per job. <b>Base</b> is the salary EPF and ETF are worked out on; <b>add-on</b> is
        the rest of the daily rate. Overtime uses base + add-on ÷ 8.
      </div>
      <div className="bar" style={{ marginBottom: 4 }}>
        <span className="sub" style={{ flex: 2 }}>Job role</span>
        <span className="sub" style={{ flex: 1, textAlign: 'right' }}>Base</span>
        <span className="sub" style={{ flex: 1, textAlign: 'right' }}>Add-on</span>
        <span style={{ flex: '0 0 78px' }} />
      </div>
      {roles.map((role, index) => (
        <div className="bar" style={{ marginBottom: 8 }} key={role.id ?? `new-${index}`}>
          <input
            type="text"
            placeholder="Tipper driver"
            value={role.name}
            onChange={(e) => setRole(index, { name: e.target.value })}
            style={{ flex: 2, minWidth: 0 }}
          />
          <input
            type="number"
            inputMode="numeric"
            placeholder="Base"
            value={role.base}
            onChange={(e) => setRole(index, { base: e.target.value })}
            style={{ flex: 1, textAlign: 'right', minWidth: 0 }}
          />
          <input
            type="number"
            inputMode="numeric"
            placeholder="Add-on"
            value={role.addon}
            onChange={(e) => setRole(index, { addon: e.target.value })}
            style={{ flex: 1, textAlign: 'right', minWidth: 0 }}
          />
          <button
            type="button"
            className="btn sm dang"
            style={{ flex: '0 0 auto', minWidth: 0 }}
            disabled={roles.length === 1}
            onClick={() => setRoles((held) => held.filter((_, i) => i !== index))}
          >
            Remove
          </button>
        </div>
      ))}
      <button
        type="button"
        className="btn sm"
        style={{ marginTop: 8 }}
        onClick={() => setRoles((held) => [...held, { id: null, name: '', base: '', addon: '' }])}
      >
        Add another role
      </button>

      {(costCodes.length > 0 || costCodeId) && (
        <>
          <label className="sub" htmlFor="worker-cost-code" style={{ display: 'block', margin: '14px 0 4px' }}>
            Usual cost code
          </label>
          <CostCodeSelect
            id="worker-cost-code"
            codes={costCodes}
            value={costCodeId}
            onChange={setCostCodeId}
          />
          <div className="sub" style={{ marginTop: 4 }}>
            His row on the time sheet starts with this code. The office can change it on the day.
          </div>
        </>
      )}

      <div className="fld" style={{ marginTop: 12, paddingBottom: 4 }}>
        <div>
          How he is paid
          <div className="sub">
            {payCycle === 'weekly'
              ? 'Paid on Saturday, for Monday to Saturday of that week.'
              : 'Paid on the monthly pay day, for the month before.'}
          </div>
        </div>
      </div>
      <div className="seg wide" role="radiogroup" aria-label="How he is paid" style={{ margin: '0 0 4px' }}>
        <button type="button" className={payCycle === 'weekly' ? 'on' : ''} aria-pressed={payCycle === 'weekly'} onClick={() => setPayCycle('weekly')}>
          Weekly
        </button>
        <button type="button" className={payCycle === 'monthly' ? 'on' : ''} aria-pressed={payCycle === 'monthly'} onClick={() => setPayCycle('monthly')}>
          Monthly
        </button>
      </div>
      {worker && isMember && worker.payCycle !== payCycle && worker.epfSalaryRs !== null && (
        <div className="banner b-warn" style={{ marginTop: 6 }}>
          His EPF salary was entered as {worker.payCycle === 'monthly' ? 'a monthly' : 'a weekly'} figure.
          Re-enter it as {payCycle === 'monthly' ? 'a monthly' : 'a weekly'} figure before saving.
        </div>
      )}

      <div className="fld" style={{ marginTop: 8 }}>
        <div>
          EPF and ETF member
          <div className="sub">Contributions are worked out on his base salary only</div>
        </div>
        <div>
          <input type="checkbox" checked={isMember} onChange={(e) => setIsMember(e.target.checked)} />
        </div>
      </div>
      {isMember && (
        <>
          <div className="fld">
            <div>EPF number</div>
            <input
              type="text"
              value={epfNo}
              onChange={(e) => setEpfNo(e.target.value)}
              style={{ width: 140, textAlign: 'left' }}
            />
          </div>
          <div className="fld">
            <div>
              EPF salary ({payCycle === 'monthly' ? 'monthly' : 'weekly'})
              <div className="sub">
                EPF/ETF is on this once his base salary earned for the{' '}
                {payCycle === 'monthly' ? 'month' : 'week'} reaches it; below it, on the base he earned.
              </div>
            </div>
            <input
              type="number" min="1" step="1" inputMode="numeric" placeholder={payCycle === 'monthly' ? 'Rs a month' : 'Rs a week'}
              value={epfSalary}
              onChange={(e) => setEpfSalary(e.target.value)}
              style={{ width: 140 }}
            />
          </div>
          <div className="fld">
            <div>
              Member from
              <div className="sub">Contributions start on this date. Days before it are left out.</div>
            </div>
            <input type="date" value={memberFrom} onChange={(e) => setMemberFrom(e.target.value)} style={{ width: 160 }} />
          </div>
          <div className="fld">
            <div>
              Stopped on
              <div className="sub">Leave empty while he is still a member</div>
            </div>
            <input type="date" value={stoppedOn} onChange={(e) => setStoppedOn(e.target.value)} style={{ width: 160 }} />
          </div>
        </>
      )}
      {!isMember && worker?.epf && (
        <div className="banner b-warn" style={{ marginTop: 6 }}>
          Saving will stop his membership today. To stop it on an earlier date, tick the box and
          fill in Stopped on instead.
        </div>
      )}

      {changes.length > 0 && (
        <>
          <div className="sub" style={{ marginTop: 10 }}>Membership changes</div>
          {changes.map((change, index) => (
            <div className="ln" key={`${change.at}-${index}`}>
              <span className="k">
                {new Date(change.at).toLocaleDateString('en-GB')} · {change.by ?? ''}
              </span>
              <span>{change.what}</span>
            </div>
          ))}
        </>
      )}

      <div className="fld" style={{ marginTop: 4 }}>
        <div>
          Active
          <div className="sub">Shows on the clerk&apos;s daily list</div>
        </div>
        <div>
          <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
        </div>
      </div>

      {problem && <div className="banner b-no" style={{ marginTop: 8 }}>{problem}</div>}

      <div className="bar" style={{ marginTop: 14 }}>
        <button type="button" className="btn" onClick={onCancel} disabled={saving}>
          Cancel
        </button>
        <button
          type="button"
          className="btn pri"
          disabled={saving}
          onClick={() => {
            setProblem('');
            const body = validate();
            if (!body) return;
            setSaving(true);
            void onSave(body).finally(() => setSaving(false));
          }}
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------- rates

/** 1st, 2nd, 3rd, 4th … 21st, 22nd, 23rd, 28th. */
function ordinal(n: number): string {
  if (n % 10 === 1 && n !== 11) return 'st';
  if (n % 10 === 2 && n !== 12) return 'nd';
  if (n % 10 === 3 && n !== 13) return 'rd';
  return 'th';
}

function RatesCard({
  rates, today, payDay, onSave, onSavePayDay,
}: {
  rates: RatesRow | null;
  today: string;
  payDay: number;
  onSave: (body: { epfEmployeePctX100: number; epfEmployerPctX100: number; etfPctX100: number }) => Promise<void>;
  onSavePayDay: (day: number) => Promise<void>;
}) {
  const [day, setDay] = useState(String(payDay));
  useEffect(() => setDay(String(payDay)), [payDay]);
  const dayNumber = Math.trunc(Number(day) || 0);
  const dayBad = dayNumber < 1 || dayNumber > 28;
  const [epfEmp, setEpfEmp] = useState(rates ? pctFromX100(rates.epfEmployeePctX100) : '8');
  const [epfEr, setEpfEr] = useState(rates ? pctFromX100(rates.epfEmployerPctX100) : '12');
  const [etf, setEtf] = useState(rates ? pctFromX100(rates.etfPctX100) : '3');
  const [problem, setProblem] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!rates) return;
    setEpfEmp(pctFromX100(rates.epfEmployeePctX100));
    setEpfEr(pctFromX100(rates.epfEmployerPctX100));
    setEtf(pctFromX100(rates.etfPctX100));
  }, [rates]);

  const unchanged =
    rates !== null &&
    pctToX100(epfEmp) === rates.epfEmployeePctX100 &&
    pctToX100(epfEr) === rates.epfEmployerPctX100 &&
    pctToX100(etf) === rates.etfPctX100;

  return (
    <div className="card">
      <h3>EPF and ETF rates</h3>
      <div className="sub">
        Sri Lankan defaults. Worked out on base salary only.
        {rates && ` In force from ${formatDate(rates.effectiveFrom)}.`}
      </div>
      <div className="fld">
        <div>EPF employee share<div className="sub">Deducted from his pay</div></div>
        <input type="number" inputMode="decimal" step="0.01" value={epfEmp} onChange={(e) => setEpfEmp(e.target.value)} style={{ width: 90 }} />
      </div>
      <div className="fld">
        <div>EPF employer share<div className="sub">Quarry pays on top</div></div>
        <input type="number" inputMode="decimal" step="0.01" value={epfEr} onChange={(e) => setEpfEr(e.target.value)} style={{ width: 90 }} />
      </div>
      <div className="fld">
        <div>ETF employer share</div>
        <input type="number" inputMode="decimal" step="0.01" value={etf} onChange={(e) => setEtf(e.target.value)} style={{ width: 90 }} />
      </div>
      {problem && <div className="banner b-no" style={{ marginTop: 8 }}>{problem}</div>}
      <div className="bar" style={{ marginTop: 8 }}>
        <button
          type="button"
          className="btn pri"
          disabled={saving || unchanged}
          onClick={() => {
            setProblem('');
            const a = pctToX100(epfEmp), b = pctToX100(epfEr), c = pctToX100(etf);
            if (a === null || b === null || c === null) {
              setProblem('Each rate is a percentage between 0 and 100.');
              return;
            }
            setSaving(true);
            void onSave({ epfEmployeePctX100: a, epfEmployerPctX100: b, etfPctX100: c }).finally(() => setSaving(false));
          }}
        >
          {saving ? 'Saving…' : 'Save rates'}
        </button>
      </div>
      <div className="sub" style={{ marginTop: 8 }}>
        New rates apply from today ({formatDate(today)}). Contributions already worked out keep
        the rate they were worked out at.
      </div>

      <div className="fld" style={{ marginTop: 10 }}>
        <div>
          Monthly pay day
          <div className="sub">
            {dayBad
              ? <span style={{ color: 'var(--text-danger)' }}>A day of the month from 1 to 28</span>
              : `Employees paid monthly are paid on the ${dayNumber}${ordinal(dayNumber)} for the month before. Employees paid weekly are paid on Saturday.`}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <input
            type="number" min="1" max="28" step="1" inputMode="numeric"
            value={day} onChange={(e) => setDay(e.target.value)}
            style={{ width: 80, ...(dayBad ? { borderColor: 'var(--danger)' } : {}) }}
          />
          <button
            type="button"
            className="btn sm"
            disabled={dayBad || dayNumber === payDay}
            onClick={() => void onSavePayDay(dayNumber)}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

// ------------------------------------------------------------ categories

function CategoriesCard({
  categories, onAdd, onToggleFlag, onRemove,
}: {
  categories: CategoryRow[];
  onAdd: (body: { name: string; requiresPlant: boolean }) => Promise<void>;
  onToggleFlag: (cat: CategoryRow) => Promise<void>;
  onRemove: (cat: CategoryRow) => Promise<void>;
}) {
  const [name, setName] = useState('');
  const [requiresPlant, setRequiresPlant] = useState(false);
  const [busy, setBusy] = useState(false);
  const active = categories.filter((c) => c.isActive);

  return (
    <div className="card">
      <h3>Misc categories</h3>
      <div className="sub">
        What the office picks from when entering an expense. Tick the ones that must be
        allocated to a plant — saving is blocked without one.
      </div>
      {active.map((cat) => (
        <div className="row" key={cat.id}>
          <div style={{ flex: 1 }}>
            <div className="name">{cat.name}</div>
            <label className="sub" style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={cat.requiresPlant}
                disabled={busy}
                onChange={() => { setBusy(true); void onToggleFlag(cat).finally(() => setBusy(false)); }}
                style={{ width: 18 }}
              />
              Must be allocated to a plant
            </label>
          </div>
          <button
            type="button"
            className="btn sm dang"
            style={{ minWidth: 0 }}
            disabled={busy}
            onClick={() => { setBusy(true); void onRemove(cat).finally(() => setBusy(false)); }}
          >
            Remove
          </button>
        </div>
      ))}
      <div className="bar" style={{ marginTop: 12, flexWrap: 'wrap' }}>
        <input type="text" placeholder="Category name" value={name} onChange={(e) => setName(e.target.value)} />
        <label className="sub" style={{ display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}>
          <input type="checkbox" checked={requiresPlant} onChange={(e) => setRequiresPlant(e.target.checked)} style={{ width: 18 }} />
          needs a plant
        </label>
        <button
          type="button"
          className="btn pri"
          disabled={busy || name.trim().length === 0}
          onClick={() => {
            setBusy(true);
            void onAdd({ name: name.trim(), requiresPlant })
              .then(() => { setName(''); setRequiresPlant(false); })
              .finally(() => setBusy(false));
          }}
        >
          Add category
        </button>
      </div>
    </div>
  );
}

// ------------------------------------------------------------ cost codes

/**
 * "I need to have a cost code defined for each plant/ employee and misc
 * expences" (11 October 2026). The list the owner keeps; each employee and
 * plant picks its usual one in its own editor.
 */
function CostCodesCard({
  costCodes, onAdd, onUpdate, onRemove,
}: {
  costCodes: CostCodeRow[];
  onAdd: (body: { code: string; name: string }) => Promise<void>;
  onUpdate: (row: CostCodeRow, body: { code: string; name: string }) => Promise<void>;
  onRemove: (row: CostCodeRow) => Promise<void>;
}) {
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState({ code: '', name: '' });
  const [removingId, setRemovingId] = useState<string | null>(null);
  const active = costCodes.filter((row) => row.isActive);

  const run = (work: () => Promise<void>, after?: () => void) => {
    setBusy(true);
    void work().then(() => after?.()).finally(() => setBusy(false));
  };

  const usedBy = (row: CostCodeRow) =>
    `${row.employees} employee${row.employees === 1 ? '' : 's'} · ${row.plants} plant${row.plants === 1 ? '' : 's'}`;

  return (
    <div className="card">
      <h3>Cost codes</h3>
      <div className="sub">
        Every cost is booked to one. Each employee and plant has a usual code; the office can
        change it on the day.
      </div>
      {active.length === 0 && <div className="empty">No cost codes yet. Add the first one below.</div>}
      {active.map((row) =>
        editingId === row.id ? (
          <div className="row" key={row.id}>
            <div className="bar" style={{ flex: 1, flexWrap: 'wrap' }}>
              <input
                type="text"
                aria-label="Code"
                value={draft.code}
                onChange={(e) => setDraft({ ...draft, code: e.target.value })}
                style={{ maxWidth: 120 }}
              />
              <input
                type="text"
                aria-label="Name"
                value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              />
              <button type="button" className="btn" disabled={busy} onClick={() => setEditingId(null)}>
                Cancel
              </button>
              <button
                type="button"
                className="btn pri"
                disabled={busy || !draft.code.trim() || !draft.name.trim()}
                onClick={() =>
                  run(() => onUpdate(row, { code: draft.code.trim(), name: draft.name.trim() }), () => setEditingId(null))
                }
              >
                Save
              </button>
            </div>
          </div>
        ) : (
          <div className="row" key={row.id}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1, minWidth: 0 }}>
              <span className="pill p-info">{row.code}</span>
              <div style={{ minWidth: 0 }}>
                <div className="name">{row.name}</div>
                {removingId === row.id ? (
                  <div className="sub" style={{ color: 'var(--text-danger)' }}>
                    Remove {row.code}? Costs already booked to it keep it.
                    {row.employees + row.plants > 0 && ` The ${usedBy(row)} using it are left with no usual code.`}
                  </div>
                ) : (
                  <div className="sub">{usedBy(row)}</div>
                )}
              </div>
            </div>
            {removingId === row.id ? (
              <div className="bar" style={{ flex: '0 0 auto' }}>
                <button type="button" className="btn sm" disabled={busy} onClick={() => setRemovingId(null)}>
                  Keep
                </button>
                <button
                  type="button"
                  className="btn sm dang"
                  disabled={busy}
                  onClick={() => run(() => onRemove(row), () => setRemovingId(null))}
                >
                  Remove
                </button>
              </div>
            ) : (
              <div className="bar" style={{ flex: '0 0 auto' }}>
                <button
                  type="button"
                  className="btn sm"
                  disabled={busy}
                  onClick={() => { setDraft({ code: row.code, name: row.name }); setEditingId(row.id); }}
                >
                  Edit
                </button>
                <button type="button" className="btn sm" disabled={busy} onClick={() => setRemovingId(row.id)}>
                  Remove
                </button>
              </div>
            )}
          </div>
        ),
      )}
      <div className="bar" style={{ marginTop: 12, flexWrap: 'wrap' }}>
        <input
          type="text"
          aria-label="Code"
          placeholder="Code"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          style={{ maxWidth: 120 }}
        />
        <input
          type="text"
          aria-label="Cost code name"
          placeholder="New cost code name"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <button
          type="button"
          className="btn pri"
          disabled={busy || !code.trim() || !name.trim()}
          onClick={() =>
            run(() => onAdd({ code: code.trim(), name: name.trim() }), () => { setCode(''); setName(''); })
          }
        >
          Add cost code
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- editor

interface EditorProps {
  user: UserRow | null;
  allPermissions: string[];
  isSelf: boolean;
  onCancel: () => void;
  onSave: (body: {
    name: string; role: 'owner' | 'clerk'; permissions: string[]; isActive: boolean;
    /** A new 4-digit PIN, or null to leave it as it is. Required for a new login. */
    pin: string | null;
  }) => Promise<void>;
  onDisable: (() => Promise<void>) | null;
}

function UserEditor({ user, allPermissions, isSelf, onCancel, onSave, onDisable }: EditorProps) {
  const [name, setName] = useState(user?.name ?? '');
  const [role, setRole] = useState<'owner' | 'clerk'>(user?.role ?? 'clerk');
  const [isActive, setIsActive] = useState(user?.isActive ?? true);
  const [permissions, setPermissions] = useState<string[]>(
    user?.role === 'owner' ? [] : user?.permissions ?? ['labour', 'diesel', 'misc', 'report'],
  );
  // A new login always gets a PIN here; an existing one only if the owner
  // opens the box to set a new one.
  const [settingPin, setSettingPin] = useState(user === null);
  const [pin, setPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [pinProblem, setPinProblem] = useState('');
  const [saving, setSaving] = useState(false);

  function pinToSave(): string | null | false {
    if (!settingPin) return null;
    try {
      assertPinShape(pin);
    } catch {
      setPinProblem('A PIN is exactly 4 digits.');
      return false;
    }
    if (pin !== confirmPin) {
      setPinProblem('The two PINs are not the same.');
      return false;
    }
    const weak = weakPinReason(pin);
    if (weak) {
      setPinProblem(weak);
      return false;
    }
    return pin;
  }

  function toggle(perm: string) {
    setPermissions((held) =>
      held.includes(perm) ? held.filter((p) => p !== perm) : [...held, perm],
    );
  }

  return (
    <div className="card">
      <h3 style={{ marginBottom: 10 }}>{user ? 'Edit login' : 'New login'}</h3>

      <div className="sub" style={{ marginBottom: 4 }}>Name</div>
      <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Office clerk" />

      <div className="fld" style={{ marginTop: 10 }}>
        <div>Access level</div>
        <select
          value={role}
          onChange={(e) => setRole(e.target.value as 'owner' | 'clerk')}
          style={{ width: 150 }}
          disabled={isSelf}
        >
          <option value="clerk">Clerk</option>
          <option value="owner">Owner</option>
        </select>
      </div>

      <div className="fld" style={{ marginTop: 10 }}>
        <div>
          {user ? 'PIN' : '4-digit PIN'}
          <div className="sub">
            {user
              ? user.hasPin
                ? `Set ${formatDate((user.pinSetAt ?? '').slice(0, 10) || '2026-01-01')}. Setting a new one signs them out everywhere.`
                : 'No PIN yet — they cannot log in until one is set.'
              : 'What they type on the tablet. Tell it to them; it is never shown again.'}
          </div>
        </div>
        {user && !settingPin ? (
          <button type="button" className="btn sm" style={{ minWidth: 0 }} onClick={() => setSettingPin(true)}>
            {user.hasPin ? 'Set a new PIN' : 'Set a PIN'}
          </button>
        ) : (
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              type="password"
              inputMode="numeric"
              autoComplete="new-password"
              maxLength={4}
              placeholder="PIN"
              value={pin}
              onChange={(e) => { setPin(e.target.value.replace(/\D/g, '')); setPinProblem(''); }}
              style={{ width: 84, letterSpacing: '0.4em', textAlign: 'center' }}
            />
            <input
              type="password"
              inputMode="numeric"
              autoComplete="new-password"
              maxLength={4}
              placeholder="again"
              value={confirmPin}
              onChange={(e) => { setConfirmPin(e.target.value.replace(/\D/g, '')); setPinProblem(''); }}
              style={{ width: 84, letterSpacing: '0.4em', textAlign: 'center' }}
            />
          </div>
        )}
      </div>
      {pinProblem && <div className="banner b-no" style={{ marginTop: 4 }}>{pinProblem}</div>}

      <div className="fld">
        <div>
          Login enabled
          {isSelf && <div className="sub">You cannot disable the login you are using.</div>}
        </div>
        <div>
          <input
            type="checkbox"
            checked={isActive}
            disabled={isSelf}
            onChange={(e) => setIsActive(e.target.checked)}
          />
        </div>
      </div>

      {role === 'owner' ? (
        <div className="banner b-info" style={{ margin: '6px 0 0' }}>
          An owner login can see and do everything, including approvals, rates and pay runs.
        </div>
      ) : (
        <>
          <div className="sub" style={{ marginBottom: 4 }}>This person can:</div>
          {allPermissions.map((perm) => (
            <label className="perm" key={perm}>
              <span>{PERMISSION_LABELS[perm] ?? perm}</span>
              <input
                type="checkbox"
                checked={permissions.includes(perm)}
                onChange={() => toggle(perm)}
              />
            </label>
          ))}
        </>
      )}

      <div className="bar" style={{ marginTop: 14 }}>
        <button type="button" className="btn" onClick={onCancel} disabled={saving}>
          Cancel
        </button>
        {onDisable && (
          <button
            type="button"
            className="btn dang"
            disabled={saving}
            onClick={() => {
              setSaving(true);
              void onDisable().finally(() => setSaving(false));
            }}
          >
            Disable
          </button>
        )}
        <button
          type="button"
          className="btn pri"
          disabled={saving || name.trim().length === 0 || (settingPin && (pin.length !== 4 || confirmPin.length !== 4))}
          onClick={() => {
            setPinProblem('');
            const chosen = pinToSave();
            if (chosen === false) return;
            setSaving(true);
            void onSave({ name: name.trim(), role, permissions, isActive, pin: chosen }).finally(() =>
              setSaving(false),
            );
          }}
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
}
